import React from 'react';
import cs from 'classnames';
import { FormikHelpers } from 'formik';

import { useReviewModules } from 'features/reviewModules/useReviewModules';
import AuthorTimeDisplay from 'shared/components/atoms/AuthorTimeDisplay';
import DeleteButton from 'shared/components/atoms/DeleteButton';
import EditButton from 'shared/components/atoms/EditButton';
import Tooltip from 'shared/components/atoms/Tooltip/NewTooltip';
import { Box, Flex, Text } from 'shared/components/display';
import { REVISION_CATEGORY_DISPLAY } from 'shared/components/organisms/ModuleReviewFormModal/config';

import { SUBMISSION_COPY } from 'shared/config/copy';
import { TEXT_VARIANTS } from 'shared/styles/text';
import { Revision, RevisionStatus, RevisionStatusDisplay } from 'shared/typings/revisions';

import ModuleRevisionForm from '../../molecules/ModuleRevisionForm';
import { ModuleRevisionValues } from '../../molecules/ModuleRevisionForm/config';

import singleRevisionStyles from './singleRevisionStyles';

interface Props {
  submissionModuleId?: number;
  revision: Revision;
  brandName: string;
  isCreator: boolean;
  revisionCount?: number;
  toggleRevisionModal?: (open: boolean | Function) => void;
}
const SingleRevision: React.FC<Props> = ({
  revision,
  brandName,
  isCreator,
  submissionModuleId,
  revisionCount,
  toggleRevisionModal,
}) => {
  const [showEditForm, setShowEditForm] = React.useState(false);
  const { canEditRevision, updateRevision, deleteRevision } = useReviewModules();

  const onEditRevision = () => {
    setShowEditForm(true);
  };

  const onCancelEdit = () => {
    setShowEditForm(false);
  };

  const handleEditSubmit = (values: ModuleRevisionValues, formikHelpers: FormikHelpers<ModuleRevisionValues>) => {
    updateRevision(
      revision.id,
      {
        category: revision.category,
        comment: values.comment,
      },
      submissionModuleId,
      () => {
        setShowEditForm(false);
        formikHelpers.setSubmitting(false);
      },
      () => {
        // Handle error
        setShowEditForm(false);
        formikHelpers.setSubmitting(false);
      },
    );
  };

  const handleDeleteConfirm = () => {
    if (revisionCount && revisionCount <= 1) {
      // If this is the last revision, close module revision modal
      if (toggleRevisionModal) {
        toggleRevisionModal(false);
      }
    }
    deleteRevision(revision.id, submissionModuleId);
  };

  const canEdit = canEditRevision(revision);
  return (
    <Box className={cs('single-revision')} css={singleRevisionStyles}>
      <Flex className="single-revision__header-group">
        <Text as="h5" className="single-revision__header">
          {REVISION_CATEGORY_DISPLAY[revision.category]}
        </Text>
        {canEdit && (
          <Flex className="single-revision__action-wrapper">
            <Tooltip
              content={SUBMISSION_COPY.MODULE_REVISION_EDIT_ICON_TOOLTIP}
              placement="top"
              portalTarget="action-wrapper"
              disabled={showEditForm}
            >
              <div className="single-revision__btn">
                <EditButton onClick={showEditForm ? onCancelEdit : onEditRevision} editing={showEditForm} hideLabel />
              </div>
            </Tooltip>
            <Tooltip
              content={SUBMISSION_COPY.MODULE_REVISION_DELETE_ICON_TOOLTIP}
              placement="top"
              portalTarget="action-wrapper"
            >
              <div className="single-revision__btn">
                <DeleteButton
                  onClick={handleDeleteConfirm}
                  deleteConfirmationText={`\n${SUBMISSION_COPY.PROMPT_DELETE_REVISION}`}
                  confirmButtonText={SUBMISSION_COPY.BUTTON_DELETE_REVISION}
                  title="Delete Revision"
                  hideLabel
                />
              </div>
            </Tooltip>
          </Flex>
        )}
      </Flex>
      {showEditForm ? (
        <ModuleRevisionForm onSubmit={handleEditSubmit} comment={revision.comment} editing={true} />
      ) : (
        <Text data-cy="revision__comment" variant={TEXT_VARIANTS.BODY}>
          {revision.comment}
        </Text>
      )}

      <Flex className="single-revision__sub-header">
        <AuthorTimeDisplay
          name={isCreator ? brandName : revision.author?.fullName}
          dateCreated={revision.dateUpdated || revision.dateCreated}
        >
          <Text
            as="span"
            className={cs('single-revision__status', {
              'single-revision__status--done': revision.status === RevisionStatus.DONE,
            })}
          >
            {RevisionStatusDisplay[revision.status]}
          </Text>
        </AuthorTimeDisplay>
      </Flex>
    </Box>
  );
};

export default SingleRevision;
